import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import * as xml2js from "xml2js";
import { ToastrService } from "ngx-toastr";
import { FileNode } from "../rule-config-component/models/rule-engine";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { HttpClient } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class DataParserService {
  constructor(
    private toastr: ToastrService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private httpClient: HttpClient
  ) {}

  private dataParserSubject = new BehaviorSubject<any>(null);
  private nodePathsSubject = new BehaviorSubject<any>(null);
  private nodesWithLeavesSubject = new BehaviorSubject<any>(null);
  private matTreeJsonSubject = new BehaviorSubject<any>(null);

  //private httpClient:HttpClient;
  public jsonData: any;
  public copyOfJsonData: any;
  uniqueId: number = 0;
  jsonForMatTree: any;
  copyOfJsonForMatTree: any;
  selectedButton: string = "";
  private xmlData: any;
  public APNodePaths: any;
  public CPNodePaths: any;

  // Clears parser cache/state so previous interface fields do not bleed into the next one.
  resetTemplateParserState() {
    this.uniqueId = 0;
    this.xmlData = null;
    this.jsonData = null;
    this.copyOfJsonData = null;
    this.jsonForMatTree = null;
    this.copyOfJsonForMatTree = null;
    this.setJsonDataForMatTree(null);
    this.setMatTreeJson([]);
    this.setNodePaths([]);
    this.setNodesWithTheirLeafNodes([]);
  }

  async parseXmlToJson(data: any) {
    let jsonData = await this.convertXmlToJson(data);
    this.setJsonDataForMatTree(jsonData);
  }

  setJsonDataForMatTree(jsonData: any) {
    this.dataParserSubject.next(jsonData);
  }

  $getJsonDataForMatTree() {
    return this.dataParserSubject.asObservable();
  }

  setNodePaths(nodePaths: any) {
    this.nodePathsSubject.next(nodePaths);
  }

  $getNodePaths = this.nodePathsSubject.asObservable();

  setMatTreeJson(jsonForMatTree: any) {
    this.matTreeJsonSubject.next(jsonForMatTree);
  }
  $getMatTreeJson = this.matTreeJsonSubject.asObservable();

  private async convertXmlToJson(xmlData: any): Promise<any> {
    const parser = new xml2js.Parser({
      strict: true,
      trim: true,
      explicitArray: true,      // Always create arrays - prevents empty elements from being skipped
      mergeAttrs: false,        // Separate attributes from nodes
      explicitCharkey: true,     // Explicitly mark text content
      charkey: '_',             // Text content stored under '_'
      attrkey: '$',             // Attributes stored under '$'
      explicitRoot: true,
      preserveChildrenOrder: true,
    });
    let convertedJsonResult = await parser.parseStringPromise(xmlData);
    return convertedJsonResult;
  }
  setNodesWithTheirLeafNodes(nodesWithLeaves: any) {
    this.nodesWithLeavesSubject.next(nodesWithLeaves);
  }
  $getNodesWithTheirLeafNodes = this.nodesWithLeavesSubject.asObservable();

  getJsonDataForMatTree(): any {
    this.$getJsonDataForMatTree().subscribe({
      next: (jsonData) => {
        if (jsonData !== null) {
          this.jsonData = jsonData;
          this.copyOfJsonData = structuredClone(this.jsonData);
          this.enableTree();
        }
      },
      error: (err) => {
        this.toastr.error("Unable to generate mat tree", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
      },
    });
  }

  private enableTree() {
    if (this.jsonData) {
      this.jsonForMatTree = this.buildFileTreeForMatTreeUsingRawJson(
        this.jsonData
      );
      this.copyOfJsonForMatTree = structuredClone(this.jsonForMatTree);
      this.setMatTreeJson(this.jsonForMatTree);
      const allPaths = this.collectAllPaths(this.jsonForMatTree);
      const uniquePaths = [...new Set(allPaths)];
      this.setNodePaths(uniquePaths);
      this.setNodesWithTheirLeafNodes(
        this.collectNodesWithLeaves(this.jsonForMatTree)
      );
    }
  }
  private buildFileTreeForMatTreeUsingRawJson(jsonData: any): FileNode[] {
    const result: FileNode[] = [];

    if (!jsonData || typeof jsonData !== 'object') {
      return result;
    }

    Object.keys(jsonData).forEach((key) => {
      const value = jsonData[key];

      if (key === '_' || key === '$') {
        return;
      }

      if (value === null || value === undefined || value === "") {
        // Empty node - create as node with empty children array
        result.push(this.createLeafNode(key));
      } else if (Array.isArray(value)) {
        // Array of elements - each item is a separate node
        value.forEach((item) => {
          const node: FileNode = { 
            key: key, 
            uniqueId: this.uniqueId++,
          };
          
          // Handle empty string in array (represents empty element)
          if (item === "" || item === null || item === undefined) {
            node.children = [];
          }
          // Process item's content (attributes and children) if it's an object
          else if (item !== null && item !== undefined && typeof item === 'object' && !Array.isArray(item)) {
            const children = this.processNodeContent(item);
            node.children = children.length > 0 ? children : [];
          } else {
            node.children = [];
          }
          
          result.push(node);
        });
      } else if (typeof value === "object") {
        // Object node - process its attributes and child nodes
        const node: FileNode = { 
          key: key, 
          uniqueId: this.uniqueId++,
        };
        
        const children = this.processNodeContent(value);
        node.children = children.length > 0 ? children : [];
        result.push(node);
      } else {
        // Primitive value (text content or empty string) - this is a NODE, not an attribute
        result.push(this.createLeafNode(key));
      }
    });

    return result;
  }

  private createLeafNode(key: string): FileNode {
    return {
      key: key,
      uniqueId: this.uniqueId++,
      children: [], // Empty node is still a NODE, not an attribute
    };
  }

  private processNodeContent(nodeData: any): FileNode[] {
    const children: FileNode[] = [];

    if (!nodeData || typeof nodeData !== 'object' || Array.isArray(nodeData)) {
      return children;
    }

    // Extract attributes from '$' object - attributes are identified by leaf: null
    if (nodeData.$ && typeof nodeData.$ === 'object' && !Array.isArray(nodeData.$)) {
      Object.keys(nodeData.$).forEach((attrName) => {
        const attrNode: FileNode = {
          key: attrName,
          uniqueId: this.uniqueId++,
          leaf: null, // Mark as attribute - this is the KEY distinction
          // NOTE: attributes do NOT have children property
        };
        children.push(attrNode);
      });
    }

    // Process all other keys as child nodes (everything except '$' and '_' is a node)
    Object.keys(nodeData).forEach((childKey) => {
      // Skip internal xml2js keys
      if (childKey === '$' || childKey === '_') {
        return;
      }

      const childValue = nodeData[childKey];

      // Case 1: null/undefined/empty string = empty node
      if (childValue === null || childValue === undefined || childValue === "") {
        children.push(this.createLeafNode(childKey));
      }
      // Case 2: Array = multiple nodes with same name
      else if (Array.isArray(childValue)) {
        childValue.forEach((item) => {
          const childNode: FileNode = {
            key: childKey,
            uniqueId: this.uniqueId++,
          };
          
          // Handle empty string in array (represents empty element like <Member></Member>)
          if (item === "" || item === null || item === undefined) {
            childNode.children = [];
          }
          // Process item's content (attributes and children) if it's an object
          else if (item !== null && item !== undefined && typeof item === 'object' && !Array.isArray(item)) {
            const itemChildren = this.processNodeContent(item);
            childNode.children = itemChildren.length > 0 ? itemChildren : [];
          } else {
            childNode.children = [];
          }
          
          children.push(childNode);
        });
      }
      // Case 3: Object = node with potential attributes and/or children
      else if (typeof childValue === "object") {
        const childNode: FileNode = {
          key: childKey,
          uniqueId: this.uniqueId++,
        };
        
        // Recursively process: extract attributes from '$' and process other keys as children
        const childNodeChildren = this.processNodeContent(childValue);
        childNode.children = childNodeChildren.length > 0 ? childNodeChildren : [];
        children.push(childNode);
      }
      // Case 4: Primitive = node with text content (text value is ignored, but it's still a node)
      else {
        children.push(this.createLeafNode(childKey));
      }
    });

    return children;
  }

  collectAllPaths(jsonForMatTree: any, path = [], paths: string[] = []): any {
    for (const item of jsonForMatTree) {
      // Skip internal keys - they should never appear in paths
      if (item.key === '$' || item.key === '_') {
        continue;
      }

      // Add the current item's name to the path
      const currentPath: any = [...path, item.key];

      // Add path for ALL nodes and attributes (not just leaf nodes)
      // Attributes get @ prefix, nodes do not
      if (item.leaf === null) {
        // This is an attribute - add @ prefix
        const modifiedPath = [...path, `@${item.key}`].join("/");
        paths.push(modifiedPath);
      } else {
        // This is a node (with or without children) - no @ prefix
        const modifiedPath = [...path, item.key].join("/");
        paths.push(modifiedPath);
      }

      // Recursively process children if they exist
      if (item.children && Array.isArray(item.children) && item.children.length > 0) {
        paths.push(...this.collectAllPaths(item.children, currentPath));
      }
    }
    return paths;
  }
  collectNodesWithLeaves(
    tree: FileNode[]
  ): { nodeName: string; leafNodes: string[] }[] {
    const nodesWithLeaves: { nodeName: string; leafNodes: string[] }[] = [];

    // Recursive function to traverse the tree
    const traverse = (treeNodes: FileNode[], currentPath: string[] = []) => {
      for (const node of treeNodes) {
        // Skip internal keys - they should never appear in paths
        if (node.key === '$' || node.key === '_') {
          continue;
        }

        const currentPathWithNode = [...currentPath, node.key];

        if (node.children && Array.isArray(node.children) && node.children.length > 0) {
          // Recursively process child nodes
          traverse(node.children, currentPathWithNode);
        } else if ((node as any).leaf === null) {
          // Leaf found - this is an attribute (leaf === null)
          // Only attributes should be added to leafNodes; empty nodes should be skipped
          this.addLeafNodeToParent(nodesWithLeaves, currentPath, node.key);
        }
        // If it's an empty node (leaf !== null), skip it - nodes should not appear in attributes list
      }
    };

    traverse(tree);
    return nodesWithLeaves;
  }

  private addLeafNodeToParent(
    nodesWithLeaves: { nodeName: string; leafNodes: string[] }[],
    currentPath: string[],
    nodeKey: string
  ): void {
    const parentNodeName = currentPath.join("/");
    const leafNode = `@${nodeKey}`;

    // Add leaf to its parent's entry or create a new one
    const parentNode = nodesWithLeaves.find(
      (n) => n.nodeName === parentNodeName
    );
    if (parentNode) {
      // Only add if not already present (prevents duplicates from multiple instances)
      if (!parentNode.leafNodes.includes(leafNode)) {
        parentNode.leafNodes.push(leafNode);
      }
    } else {
      nodesWithLeaves.push({
        nodeName: parentNodeName,
        leafNodes: [leafNode],
      });
    }
  }

  public loadCPData() {
    this.resetTemplateParserState();
    this.selectedButton = "CP";
    const filePath = "/assets/rule-config/CP.xml"; // Update this with your file path //src\assets\rule-config\AP.xml
    //this.fileName = "CP";

    this.ngxUiLoaderService.start();
    this.httpClient
      .get(filePath, { responseType: "text" }) // or 'json' for JSON files
      .subscribe({
        next: (data) => {
          this.ngxUiLoaderService.stop();
          this.xmlData = data;
          this.parseXmlToJson(this.xmlData);
          this.getJsonDataForMatTree();

          //we are now loading the nodepaths of CP to the CPData variable
          this.$getNodePaths.subscribe({
            next: (data: any) => {
              this.CPNodePaths = data;
            },
          });
          this.ngxUiLoaderService.stop();
        },
        error: (err) => {
          this.ngxUiLoaderService.stop();
          this.toastr.error("Unable to load CP data", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
  }

  public loadResultOutboundData() {
    this.resetTemplateParserState();
    this.selectedButton = "CP";
    const filePath = "/assets/rule-config/RESULT_OUTBOUND.xml";

    this.ngxUiLoaderService.start();
    this.httpClient
      .get(filePath, { responseType: "text" })
      .subscribe({
        next: (data) => {
          this.ngxUiLoaderService.stop();
          this.xmlData = data;
          this.parseXmlToJson(this.xmlData);
          this.getJsonDataForMatTree();
        },
        error: () => {
          this.ngxUiLoaderService.stop();
          this.toastr.error("Unable to load Result Outbound template", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
  }

  public loadAPData() {
    this.resetTemplateParserState();
    this.selectedButton = "AP";
    const filePath = "/assets/rule-config/AP.xml"; // Update this with your file path
    //this.fileName = "AP";
    this.ngxUiLoaderService.start();
    //this.fetchData();
    this.httpClient
      .get(filePath, { responseType: "text" }) // or 'json' for JSON files
      .subscribe({
        next: (data) => {
          this.xmlData = data;
          this.parseXmlToJson(this.xmlData);
          this.getJsonDataForMatTree();

          //we are now loading the nodepaths of AP to the APData variable
          this.$getNodePaths.subscribe({
            next: (data: any) => {
              this.APNodePaths = data;
            },
          });
          this.ngxUiLoaderService.stop();
        },
        error: (err) => {
          this.ngxUiLoaderService.stop();
          this.toastr.error("Unable to load AP data", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
  }
}
